import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, ProductToggleChangeEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';

/**
 * How long to hold a toggled row still, in milliseconds. Must outlast the
 * `.product-toggle__options` open/close transition in `blocks/product-toggle-item.liquid`.
 */
const ROW_PINNING_DURATION = 380;

/**
 * Global manager for product toggles that works with standalone toggle items
 */
class ProductToggleManager {
  /** @type {ProductToggleManager | null} */
  static #instance = null;
  #isProcessing = false;
  #isInitialized = false;
  /** @type {(() => void) | null} */
  #cancelRowPinning = null;
  /** @type {HTMLTemplateElement | null} */
  #moneyFormatTemplate = null;

  static getInstance() {
    if (!ProductToggleManager.#instance) {
      ProductToggleManager.#instance = new ProductToggleManager();
    }
    return ProductToggleManager.#instance;
  }

  initialize() {
    if (this.#isInitialized) return;
    this.#isInitialized = true;

    // Find money format template from shop.money_format (we'll create it if needed)
    this.#createMoneyFormatTemplate();

    // Listen for cart add events from the main product form
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd.bind(this));

    // Listen for toggle changes on the document
    document.addEventListener('change', this.#handleToggleChange.bind(this));

    // Re-measure a group's drawer slideshow once its dialog is actually open
    document.addEventListener('click', this.#handleInfoButtonClick.bind(this));

    // A listener that connects after us can ask for the current state instead of waiting for
    // the next toggle. Together with the broadcast below this covers both load orders.
    document.addEventListener(ThemeEvents.productToggleRequest, this.#broadcastToggleState.bind(this));

    // Announce the initial state so listeners (e.g. the mobile sticky buy bar) start in sync.
    this.#broadcastToggleState();
  }

  #createMoneyFormatTemplate() {
    // Try to find existing template
    const existingTemplate = document.querySelector('template[ref="moneyFormat"]');
    if (existingTemplate instanceof HTMLTemplateElement) {
      this.#moneyFormatTemplate = existingTemplate;
      return;
    }
  }

  /**
   * The element carrying the base product's pricing data. The group wrapper owns it; older
   * markup duplicated it onto every row, so fall back to the first row.
   * @returns {HTMLElement | null}
   */
  #getBaseDataElement() {
    const group = document.querySelector('.product-toggle__group');
    if (group instanceof HTMLElement && group.dataset.basePrice != null) return group;

    const firstToggleItem = document.querySelector('.product-toggle__item');
    return firstToggleItem instanceof HTMLElement ? firstToggleItem : null;
  }

  #getBasePrice() {
    const baseElement = this.#getBaseDataElement();
    if (!baseElement) return 0;

    // Prefer the live campaign/VIP unit price applied by campaign-price.js — the source of
    // truth during a campaign. It annotates the main price element with the effective price
    // (VIP or public) in minor units; toggles must build on that, not the normal price.
    const campaignCents = this.#getActiveCampaignBaseCents();
    if (campaignCents > 0) return campaignCents;

    const basePrice = Number(baseElement.dataset.basePrice || '0');
    return Number.isNaN(basePrice) ? 0 : basePrice;
  }

  /**
   * The effective campaign/VIP unit price (minor units) that campaign-price.js applied to the
   * main product price element, or 0 when no campaign is currently active on this page.
   * @returns {number}
   */
  #getActiveCampaignBaseCents() {
    const { priceElements } = this.#getPriceElements();
    for (const priceEl of priceElements) {
      const cents = Number(priceEl.dataset.campaignPriceCents || '0');
      if (!Number.isNaN(cents) && cents > 0) return cents;
    }
    return 0;
  }

  /**
   * The "Spar" label, read from the shared #campaign-config JSON (same source campaign-price.js
   * uses) so the toggle badge text matches it exactly.
   * @returns {string}
   */
  #getSaveLabel() {
    const el = document.getElementById('campaign-config');
    if (!el?.textContent) return '';
    try {
      const cfg = JSON.parse(el.textContent);
      return typeof cfg.labelSave === 'string' ? cfg.labelSave : '';
    } catch {
      return '';
    }
  }

  /**
   * Keep the campaign "Spar X" savings badge in sync with the toggled total. Fills the same
   * `[data-campaign-savings]` slot campaign-price.js owns, in the identical "label amount"
   * format, so main discount + on-sale add-ons are reflected. Leaves the badge untouched when
   * there is nothing to save, so it never fights campaign-price.js's hidden (no-campaign) state.
   * @param {number} savings - Total savings in minor units.
   */
  #updateSavingsBadge(savings) {
    const section = document.querySelector('.product-information') || document.querySelector('.shopify-section');
    if (!section) return;

    const badges = section.querySelectorAll('[data-campaign-savings]');
    if (!badges.length || savings <= 0) return;

    const label = this.#getSaveLabel();
    const text = `${label} ${this.#formatMoney(savings)}`.trim();

    for (const badge of badges) {
      if (!(badge instanceof HTMLElement)) continue;
      badge.textContent = text;
      badge.style.display = '';
    }
  }

  #getBaseComparePrice() {
    const baseElement = this.#getBaseDataElement();
    if (!baseElement) return 0;

    // When a campaign is active, the original (full/normal) price becomes the struck-through
    // compare-at.
    if (this.#getActiveCampaignBaseCents() > 0) {
      const fullPrice = Number(baseElement.dataset.basePrice || '0');
      if (!Number.isNaN(fullPrice) && fullPrice > 0) return fullPrice;
    }

    const baseCompare = Number(baseElement.dataset.baseComparePrice || '0');

    if (!Number.isNaN(baseCompare) && baseCompare > 0) {
      return baseCompare;
    }

    // If there is no compare-at price, fall back to base price so we don't show a discount
    return this.#getBasePrice();
  }

  #getCurrency() {
    const baseElement = this.#getBaseDataElement();
    if (!baseElement) return '';
    return baseElement.dataset.currency || '';
  }

  /**
   * Handles CartAddEvent from product-form-component
   * @param {CartAddEvent} event - The cart add event
   */
  async #handleCartAdd(event) {
    // Only process events from product-form-component (main product add)
    if (event.detail?.data?.source !== 'product-form-component') {
      return;
    }

    // Prevent multiple simultaneous processing
    if (this.#isProcessing) {
      return;
    }

    // Get all toggled products
    const toggledProducts = this.#getToggledProducts();
    if (toggledProducts.length === 0) {
      return;
    }

    this.#isProcessing = true;

    try {
      // Add each toggled product to cart sequentially
      for (const productData of toggledProducts) {
        await this.#addProductToCart(productData);
      }

      // After cart updates, also ensure the price preview reflects current toggles
      this.#updateDisplayedPrice();
    } catch (error) {
      console.error('Error adding toggled products to cart:', error);
    } finally {
      this.#isProcessing = false;
    }
  }

  /**
   * Handles toggle change events — both a row's master switch and, in group mode, the
   * individual option cards — then recalculates the displayed price.
   * @param {Event} event
   */
  #handleToggleChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    const isMaster = target.classList.contains('product-toggle__input');
    const isOption = target.classList.contains('product-toggle__card-input');
    if (!isMaster && !isOption) return;

    const item = target.closest('.product-toggle__item');

    // Opening a group panel or revealing a compare-at price changes the page height, which
    // yanks the page up or down under the customer. Measure the row first, then put it back
    // where it was once everything has settled.
    const anchor = item instanceof HTMLElement ? item : null;
    const anchorTop = anchor ? anchor.getBoundingClientRect().top : 0;

    if (item instanceof HTMLElement && item.dataset.mode === 'group') {
      if (isMaster) {
        this.#syncGroupFromMaster(item, target);
      } else {
        this.#syncGroupFromOption(item);
      }
    }

    if (isMaster) this.#syncDisclosureState(target);

    this.#updateDisplayedPrice();

    if (anchor) this.#keepRowInPlace(anchor, anchorTop);
  }

  /**
   * Holds the toggled row still in the viewport while everything around it reflows.
   *
   * A single correction is not enough. The compare-at price reflows synchronously, the price
   * text is swapped in on the next frame, and the options panel animates open or shut over
   * roughly a quarter of a second — and on every one of those frames the browser's own scroll
   * anchoring moves the page to keep some *other* element still, which is exactly what threw
   * the customer to the wrong place. So we re-measure and re-correct for the whole animation,
   * and bail out the moment the customer scrolls themselves.
   *
   * @param {HTMLElement} anchor - The toggle row that was interacted with.
   * @param {number} anchorTop - The row's viewport offset before the change.
   */
  #keepRowInPlace(anchor, anchorTop) {
    this.#cancelRowPinning?.();

    let cancelled = false;
    const deadline = performance.now() + ROW_PINNING_DURATION;

    const stop = () => {
      cancelled = true;
      window.removeEventListener('wheel', stop);
      window.removeEventListener('touchstart', stop);
      window.removeEventListener('keydown', stop);
      if (this.#cancelRowPinning === stop) this.#cancelRowPinning = null;
    };

    // Never fight the customer for control of the scroll position.
    window.addEventListener('wheel', stop, { once: true, passive: true });
    window.addEventListener('touchstart', stop, { once: true, passive: true });
    window.addEventListener('keydown', stop, { once: true });
    this.#cancelRowPinning = stop;

    const correct = () => {
      if (cancelled) return;

      const delta = anchor.getBoundingClientRect().top - anchorTop;
      if (Math.abs(delta) >= 1) {
        window.scrollTo({ top: window.scrollY + delta, behavior: 'instant' });
      }

      if (performance.now() < deadline) {
        requestAnimationFrame(correct);
      } else {
        stop();
      }
    };

    correct();
  }

  /**
   * A group's drawer slideshow lays itself out inside a closed `<dialog>`, which has no
   * dimensions, so it measures at zero width and opens scrolled to the wrong place. Re-snap
   * it to its current slide once the dialog is open and has real size.
   * @param {Event} event
   */
  #handleInfoButtonClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('.product-toggle__info-button');
    if (!button) return;

    const slideshow = button.closest('.product-toggle__info')?.querySelector('slideshow-component');
    if (!slideshow) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (typeof slideshow.select !== 'function') return;
        slideshow.select(slideshow.current || 0, undefined, { animate: false });
      });
    });
  }

  /**
   * Mirrors a group master switch's checked state onto the option panel's disclosure state.
   * Single-product rows have no panel and carry no aria-expanded.
   * @param {HTMLInputElement} master
   */
  #syncDisclosureState(master) {
    if (!master.hasAttribute('aria-expanded')) return;
    master.setAttribute('aria-expanded', String(master.checked));
  }

  /**
   * Master switch flipped on a group row: switching on preselects the first option that can
   * actually be bought, switching off clears the row's radio selection — the only way back to
   * "no add-on from this group" once a card has been picked.
   * @param {HTMLElement} item
   * @param {HTMLInputElement} master
   */
  #syncGroupFromMaster(item, master) {
    const options = this.#getGroupOptions(item);

    if (!master.checked) {
      for (const option of options) option.checked = false;
      return;
    }

    if (options.some((option) => option.checked)) return;

    const firstSelectable = options.find((option) => !option.disabled);
    if (firstSelectable) firstSelectable.checked = true;
  }

  /**
   * An option card was picked: the master switch follows the row's selection, so picking a
   * card keeps the row open. The cards are radios, so a customer can swap between options but
   * cannot clear the row from inside the panel — the master switch does that. The
   * no-selection branch below only fires if a row somehow ends up with nothing checked.
   * @param {HTMLElement} item
   */
  #syncGroupFromOption(item) {
    const master = item.querySelector('.product-toggle__input');
    if (!(master instanceof HTMLInputElement)) return;

    const hasSelection = this.#getGroupOptions(item).some((option) => option.checked);
    if (master.checked === hasSelection) return;

    master.checked = hasSelection;
    this.#syncDisclosureState(master);

    // The panel is about to be hidden with the just-clicked card still focused inside it.
    // Hand focus back to the master switch so it does not fall to <body> and drag the
    // viewport with it.
    if (!hasSelection && item.contains(document.activeElement)) {
      master.focus({ preventScroll: true });
    }
  }

  /**
   * The option-card radios belonging to one group row.
   * @param {HTMLElement} item
   * @returns {HTMLInputElement[]}
   */
  #getGroupOptions(item) {
    /** @type {HTMLInputElement[]} */
    const options = [];
    for (const option of item.querySelectorAll('.product-toggle__card-input')) {
      if (option instanceof HTMLInputElement) options.push(option);
    }
    return options;
  }

  /**
   * Recalculates the total price (base product + toggles) and updates the visible price
   */
  #updateDisplayedPrice() {
    // Broadcast first: listeners must stay in sync even for products with no base price,
    // where the price-element update below is skipped.
    this.#broadcastToggleState();

    const basePrice = this.#getBasePrice();
    if (!basePrice) return;

    const extraPrice = this.#getExtraPriceFromToggles();
    const extraCompare = this.#getExtraCompareFromToggles();

    const totalPrice = basePrice + extraPrice;
    const totalCompare = this.#getBaseComparePrice() + extraCompare;

    // Keep the "Spar X" savings badge in sync: total savings = the main campaign/VIP discount
    // plus any on-sale add-ons (totalCompare − totalPrice).
    this.#updateSavingsBadge(totalCompare - totalPrice);

    const { priceElements, compareElements } = this.#getPriceElements();
    if (!priceElements.length) return;

    const formattedPrice = this.#formatMoney(totalPrice);
    const formattedCompare = this.#formatMoney(totalCompare);

    // Update all compare-at elements first (before price animation)
    compareElements.forEach((compareElement) => {
      if (totalCompare > totalPrice) {
        compareElement.textContent = formattedCompare;
        // Show the parent role="group" span if it exists
        const parentGroup = compareElement.closest('span[role="group"]');
        if (parentGroup instanceof HTMLElement) {
          parentGroup.style.display = '';
          parentGroup.classList.remove('hidden');
        } else if (compareElement.parentElement) {
          compareElement.parentElement.style.display = '';
          compareElement.parentElement.classList.remove('hidden');
        }
      } else {
        compareElement.textContent = '';
        // Hide the parent role="group" span if it exists
        const parentGroup = compareElement.closest('span[role="group"]');
        if (parentGroup instanceof HTMLElement) {
          parentGroup.style.display = 'none';
          parentGroup.classList.add('hidden');
        } else if (compareElement.parentElement) {
          compareElement.parentElement.style.display = 'none';
          compareElement.parentElement.classList.add('hidden');
        }
      }
    });

    // Update all visible price elements (desktop, mobile, sticky, etc.)
    // Use requestAnimationFrame to ensure animation triggers after DOM updates
    requestAnimationFrame(() => {
      priceElements.forEach((priceElement) => {
        priceElement.textContent = formattedPrice;

        // Find the parent role="group" if it exists (when there's a compare-at price)
        // Otherwise use the price element itself
        const animationTarget = priceElement.closest('span[role="group"]') || priceElement;

        // Remove any existing animation class first to allow re-triggering
        if (animationTarget instanceof HTMLElement) {
          animationTarget.classList.remove('price--updated');
        }

        // Use a second requestAnimationFrame to ensure the class removal is processed
        requestAnimationFrame(() => {
          // Trigger a subtle animation so users notice the price change
          if (animationTarget instanceof HTMLElement) {
            animationTarget.classList.add('price--updated');
            animationTarget.addEventListener(
              'animationend',
              () => {
                animationTarget.classList.remove('price--updated');
              },
              { once: true }
            );
          }
        });
      });
    });
  }

  /**
   * Every checked, selectable add-on on the page. Liquid marks each one with
   * `data-toggle-selection` and puts the price and variant data on the input itself — the
   * master switch for a single-product row, the option cards for a group row — so one
   * selector covers both modes.
   * @returns {HTMLInputElement[]}
   */
  #getSelectedInputs() {
    /** @type {HTMLInputElement[]} */
    const selected = [];
    for (const input of document.querySelectorAll('input[data-toggle-selection]:checked:not(:disabled)')) {
      if (input instanceof HTMLInputElement) selected.push(input);
    }
    return selected;
  }

  /**
   * The same selections, in DOM order, described for display. `data-label` and
   * `data-price-formatted` are rendered by blocks/product-toggle-item.liquid so listeners never
   * have to parse visible text; a group option's label already reads "Group · Option".
   * @returns {import('@theme/events').ProductToggleSelection[]}
   */
  #getSelectedItems() {
    /** @type {import('@theme/events').ProductToggleSelection[]} */
    const items = [];

    for (const input of this.#getSelectedInputs()) {
      const price = Number(input.dataset.price || '0');

      items.push({
        label: input.dataset.label || '',
        price: Number.isNaN(price) ? 0 : price,
        formattedPrice: input.dataset.priceFormatted || '',
      });
    }

    return items;
  }

  /**
   * Tells the rest of the page which add-ons are selected and what the running total is.
   * This manager owns both the arithmetic (campaign/VIP aware) and the money formatting, so
   * listeners such as the mobile sticky buy bar only have to render what they are handed.
   */
  #broadcastToggleState() {
    const items = this.#getSelectedItems();
    const total = this.#getBasePrice() + this.#getExtraPriceFromToggles();

    document.dispatchEvent(
      new ProductToggleChangeEvent({
        items,
        total,
        formattedTotal: this.#formatMoney(total),
      })
    );
  }

  /**
   * Gets the additional price from all checked toggles
   * @returns {number}
   */
  #getExtraPriceFromToggles() {
    let extra = 0;

    for (const input of this.#getSelectedInputs()) {
      const price = Number(input.dataset.price || '0');
      if (!Number.isNaN(price)) extra += price;
    }

    return extra;
  }

  /**
   * Gets the additional compare-at price from all checked toggles
   * @returns {number}
   */
  #getExtraCompareFromToggles() {
    let extra = 0;

    for (const input of this.#getSelectedInputs()) {
      const compare = Number(input.dataset.comparePrice || '0');

      if (!Number.isNaN(compare) && compare > 0) {
        extra += compare;
      } else {
        // If no compare-at price, fall back to regular price
        const price = Number(input.dataset.price || '0');
        if (!Number.isNaN(price)) extra += price;
      }
    }

    return extra;
  }

  /**
   * Finds all product price and compare-at elements on the page
   * @returns {{ priceElements: HTMLElement[], compareElements: HTMLElement[] }}
   */
  #getPriceElements() {
    const section = document.querySelector('.product-information') || document.querySelector('.shopify-section');
    if (!section) {
      return { priceElements: [], compareElements: [] };
    }

    const priceNodeList = section.querySelectorAll('product-price [ref="priceContainer"] .price');
    const compareNodeList = section.querySelectorAll('product-price [ref="priceContainer"] .compare-at-price');

    /** @type {HTMLElement[]} */
    const priceElements = [];
    priceNodeList.forEach((el) => {
      if (el instanceof HTMLElement) priceElements.push(el);
    });

    /** @type {HTMLElement[]} */
    const compareElements = [];
    compareNodeList.forEach((el) => {
      if (el instanceof HTMLElement) compareElements.push(el);
    });

    return {
      priceElements,
      compareElements,
    };
  }

  /**
   * Formats a money value (in minor units) using the shop's money format template
   * Falls back to a simple Intl formatter if needed.
   * @param {number} moneyValue
   * @returns {string}
   */
  #formatMoney(moneyValue) {
    if (this.#moneyFormatTemplate instanceof HTMLTemplateElement) {
      const template = this.#moneyFormatTemplate.content.textContent || '{{amount}}';
      const currency = (this.#getCurrency() || '').toUpperCase();

      const DEFAULT_CURRENCY_DECIMALS = 2;
      /** @type {Record<string, number>} */
      const CURRENCY_DECIMALS = {
        BHD: 3,
        BIF: 0,
        BYR: 0,
        CLF: 4,
        CLP: 0,
        DJF: 0,
        GNF: 0,
        IQD: 3,
        ISK: 0,
        JOD: 3,
        JPY: 0,
        KMF: 0,
        KRW: 0,
        KWD: 3,
        LYD: 3,
        MRO: 5,
        OMR: 3,
        PYG: 0,
        RSD: 0,
        RWF: 0,
        TND: 3,
        UGX: 0,
        UYI: 0,
        VEF: 0,
        VND: 0,
        VUV: 0,
        XAF: 0,
        XOF: 0,
        XPF: 0,
      };

      const precision = CURRENCY_DECIMALS[currency] ?? DEFAULT_CURRENCY_DECIMALS;

      return template.replace(/{{\s*(\w+)\s*}}/g, (_match, placeholder) => {
        if (typeof placeholder !== 'string') return '';
        if (placeholder === 'currency') return currency;

        let thousandsSeparator = ',';
        let decimalSeparator = '.';
        let localPrecision = precision;

        if (placeholder === 'amount') {
          // Defaults
        } else if (placeholder === 'amount_no_decimals') {
          localPrecision = 0;
        } else if (placeholder === 'amount_with_comma_separator') {
          thousandsSeparator = '.';
          decimalSeparator = ',';
        } else if (placeholder === 'amount_no_decimals_with_comma_separator') {
          thousandsSeparator = '.';
          localPrecision = 0;
        } else if (placeholder === 'amount_no_decimals_with_space_separator') {
          thousandsSeparator = ' ';
          localPrecision = 0;
        }

        const value = moneyValue / Math.pow(10, precision);
        const fixed = value.toFixed(localPrecision);
        const parts = fixed.split('.');
        const whole = parts[0] || '0';
        const fraction = parts[1] || '';

        const withThousands = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);
        if (localPrecision === 0) return withThousands;

        // Trim trailing zeros from the decimal part (money_without_trailing_zeros behaviour)
        const trimmedFraction = fraction.replace(/0+$/, '');
        if (!trimmedFraction) return withThousands;

        return `${withThousands}${decimalSeparator}${trimmedFraction}`;
      });
    }

    // Fallback using Intl
    try {
      const amount = moneyValue / 100;
      const formatted = new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: this.#getCurrency() || 'USD',
        maximumFractionDigits: 2,
      }).format(amount);

      // Best-effort trimming of trailing zeros in the locale format
      return formatted
        .replace(/([.,]\d*?[1-9])0+([^\d]|$)/, '$1$2')
        .replace(/([.,])0+([^\d]|$)/, '$2');
    } catch {
      return String(moneyValue / 100);
    }
  }

  /**
   * Gets all products that have their toggle switched on
   * @returns {Array<{variantId: string, productId: string}>}
   */
  #getToggledProducts() {
    /** @type {Array<{variantId: string, productId: string}>} */
    const toggledProducts = [];

    for (const input of this.#getSelectedInputs()) {
      const { variantId, productId } = input.dataset;
      if (variantId && productId) toggledProducts.push({ variantId, productId });
    }

    return toggledProducts;
  }

  /**
   * Adds a product to the cart
   * @param {{variantId: string, productId: string}} productData - The product data
   */
  async #addProductToCart(productData) {
    const { variantId, productId } = productData;

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');

    // Get cart sections to update
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    /** @type {string[]} */
    const cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });

    if (cartItemComponentsSectionIds.length > 0) {
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    }

    const fetchCfg = fetchConfig('javascript', { body: formData });

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: 'text/html',
        },
      });

      const responseData = await response.json();

      if (responseData.status) {
        // Error adding to cart
        document.dispatchEvent(
          new CartErrorEvent(
            `product-toggle-${variantId}`,
            responseData.message,
            responseData.description,
            responseData.errors
          )
        );
        console.error('Failed to add toggled product to cart:', responseData.message);
      } else {
        // Successfully added to cart
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();

        document.dispatchEvent(
          new CartAddEvent({}, variantId.toString(), {
            source: 'product-toggle-component',
            itemCount: cart.item_count,
            productId: productId,
            sections: responseData.sections,
          })
        );

        cartPerformance.measureFromEvent('add:user-action', new Event('toggle-add'));
      }
    } catch (error) {
      console.error('Error adding product to cart:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      document.dispatchEvent(
        new CartErrorEvent(
          `product-toggle-${variantId}`,
          'Failed to add product to cart',
          errorMessage,
          {}
        )
      );
    }
  }
}

/**
 * A custom element that initializes the global toggle manager when a toggle item is present
 * @extends {Component}
 */
class ProductToggleComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    // Initialize the global manager when any toggle component is connected
    ProductToggleManager.getInstance().initialize();
  }
}

if (!customElements.get('product-toggle-component')) {
  customElements.define('product-toggle-component', ProductToggleComponent);
}

// Also initialize on DOMContentLoaded in case toggle items are standalone
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    if (document.querySelector('.product-toggle__item')) {
      ProductToggleManager.getInstance().initialize();
    }
  });
} else {
  if (document.querySelector('.product-toggle__item')) {
    ProductToggleManager.getInstance().initialize();
  }
}
